export interface MenuProduct {
  // Core fields - always present
  id: string;
  name: string;
  category: string;
  subcategory: string;
  
  // Optional fields with defaults (matching backend FrontendProduct)
  strainType: string | null;
  description: string | null;
  descriptionHtml: string | null;
  createdAt: string | null;
  enterpriseProductId: string | null;
  productBatchId: string | null;
  slug: string | null;
  posId: string | null;
  staffPick: boolean;
  tags: string[];
  menuTypes: string[];
  
  // Complex objects with safe defaults
  images: {
    id: string | null;
    url: string;
    label: string | null;
    description: string | null;
  }[];
  brand: {
    id: string;
    name: string;
    description: string | null;
    imageUrl: string | null;
  } | null;
  variants: {
    id: string;
    option: string;
    priceMed: number | null;
    priceRec: number | null;
    specialPriceMed: number | null;
    specialPriceRec: number | null;
    quantity: number | null;
    specialConditionIds: string[];
    specialRewardIds: string[];
    flowerEquivalent: {
      value: number | null;
      unit: string | null;
    } | null;
  }[];
  potencyCbd: {
    formatted: string | null;
    range: string[] | null;
    unit: string | null;
  } | null;
  potencyThc: {
    formatted: string | null;
    range: string[] | null;
    unit: string | null;
  } | null;
  effects: string[];
  terpenes: {
    id: string | null;
    name: string | null;
    description: string | null;
    unitSymbol: string | null;
    value: number | null;
  }[];
  cannabinoids: {
    id: string | null;
    name: string | null;
    description: string | null;
    unitSymbol: string | null;
    value: number | null;
  }[];
  posMetaData: {
    id: string | null;
    category: string | null;
    sku: string | null;
    batchId: number | null;
    batchName: string | null;
    alternateName: string | null;
    labResultUrl: string | null;
    packagedDate: string | null;
  } | null;
  productAvailabilityByRetailer: {
    isAvailable: boolean;
    retailer: {
      id: string;
      name: string;
    };
    productId: string;
  }[];

  // Debug field - raw Dutchie response (development only)
  _dutchieRaw?: any;
}

interface MenuResponse {
  products: MenuProduct[];
  count: number;
  totalCount: number;
}

export class MenuService {
  private static baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api`;

  public static async getMenuProducts(retailerId?: string): Promise<MenuResponse> {
    // Build URL with retailer ID parameter if provided
    const url = retailerId ? 
      `${this.baseUrl}/menu/products?retailerId=${encodeURIComponent(retailerId)}` : 
      `${this.baseUrl}/menu/products`;
    
    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      const parsedResponse = this.parseMenuResponse(data);

      return parsedResponse;
    } catch (error) {
      console.error('Error in getMenuProducts:', error);
      throw error;
    }
  }

  private static parseMenuResponse(data: any): MenuResponse {
    // Handle case where response is a direct array of products
    if (Array.isArray(data)) {
      const products = data.map((item) => this.parseProduct(item));
      return {
        products,
        count: data.length,
        totalCount: data.length
      };
    }

    // Handle case where response is nested under data property
    if (data?.data && Array.isArray(data.data.products)) {
      const products = data.data.products.map((item: any) => this.parseProduct(item));
      return {
        products,
        count: data.data.count || data.data.products.length,
        totalCount: data.data.totalCount || data.data.products.length
      };
    }

    // Handle case where response is an object with products array
    if (data && Array.isArray(data.products)) {
      const products = data.products.map((item: any) => this.parseProduct(item));
      return {
        products,
        count: data.count || data.products.length,
        totalCount: data.totalCount || data.products.length
      };
    }

    console.error('Invalid menu response format');
    throw new Error('Invalid menu response format');
  }

  private static parseProduct(product: any): MenuProduct {
    // Validate required fields and provide defaults for optional ones
    if (!product.id || !product.name) {
      console.error('Invalid product data: missing required fields', product.id, product.name);
      throw new Error('Invalid product data: missing required fields');
    }

    const parsedProduct = {
      // Core fields
      id: product.id,
      name: product.name,
      category: product.category || '',
      subcategory: product.subcategory || '',
      
      // Optional fields with safe defaults
      strainType: product.strainType || null,
      description: product.description || null,
      descriptionHtml: product.descriptionHtml || null,
      createdAt: product.createdAt || null,
      enterpriseProductId: product.enterpriseProductId || null,
      productBatchId: product.productBatchId || null,
      slug: product.slug || null,
      posId: product.posId || null,
      staffPick: product.staffPick || false,
      tags: Array.isArray(product.tags) ? product.tags : [],
      menuTypes: Array.isArray(product.menuTypes) ? product.menuTypes : [],
      
      // Complex objects with safe mapping
      images: Array.isArray(product.images) ? product.images.map((img: any) => ({
        id: img.id || null,
        url: img.url || '',
        label: img.label || null,
        description: img.description || null
      })) : [],
      brand: product.brand ? {
        id: product.brand.id || '',
        name: product.brand.name || '',
        description: product.brand.description || null,
        imageUrl: product.brand.imageUrl || null
      } : null,
      variants: Array.isArray(product.variants) ? product.variants.map((variant: any) => ({
        id: variant.id || '',
        option: variant.option || '',
        priceMed: variant.priceMed || null,
        priceRec: variant.priceRec || null,
        specialPriceMed: variant.specialPriceMed || null,
        specialPriceRec: variant.specialPriceRec || null,
        quantity: variant.quantity || null,
        specialConditionIds: Array.isArray(variant.specialConditionIds) ? variant.specialConditionIds : [],
        specialRewardIds: Array.isArray(variant.specialRewardIds) ? variant.specialRewardIds : [],
        flowerEquivalent: variant.flowerEquivalent ? {
          value: variant.flowerEquivalent.value || null,
          unit: variant.flowerEquivalent.unit || null
        } : null
      })) : [],
      potencyCbd: product.potencyCbd ? {
        formatted: product.potencyCbd.formatted || null,
        range: Array.isArray(product.potencyCbd.range) ? product.potencyCbd.range : null,
        unit: product.potencyCbd.unit || null
      } : null,
      potencyThc: product.potencyThc ? {
        formatted: product.potencyThc.formatted || null,
        range: Array.isArray(product.potencyThc.range) ? product.potencyThc.range : null,
        unit: product.potencyThc.unit || null
      } : null,
      effects: Array.isArray(product.effects) ? product.effects : [],
      terpenes: Array.isArray(product.terpenes) ? product.terpenes
        .filter((terpene: any) => terpene !== null && terpene !== undefined)
        .map((terpene: any) => ({
          id: terpene.id || null,
          name: terpene.name || terpene.terpene?.name || null,
          terpeneId: terpene.terpeneId || null,
          unit: terpene.unit || null,
          unitSymbol: terpene.unitSymbol || null,
          value: terpene.value || null,
          // Preserve the full nested terpene object for debug view
          terpene: terpene.terpene || null,
          // Also keep simplified fields for backward compatibility
          description: terpene.terpene?.description || null
        })) : [],
      cannabinoids: Array.isArray(product.cannabinoids) ? product.cannabinoids
        .filter((cannabinoid: any) => cannabinoid !== null && cannabinoid !== undefined)
        .map((cannabinoid: any) => ({
          cannabinoidId: cannabinoid.cannabinoidId || null,
          unit: cannabinoid.unit || null,
          value: cannabinoid.value || null,
          // Preserve the full nested cannabinoid object for debug view
          cannabinoid: cannabinoid.cannabinoid || null,
          // Also keep simplified fields for backward compatibility
          id: cannabinoid.cannabinoidId || null,
          name: cannabinoid.cannabinoid?.name || cannabinoid.name || null,
          description: cannabinoid.cannabinoid?.description || cannabinoid.description || null,
          unitSymbol: (cannabinoid.unit && cannabinoid.unit.toString()) || cannabinoid.unitSymbol || null
        })) : [],
      posMetaData: product.posMetaData ? {
        id: product.posMetaData.id || null,
        category: product.posMetaData.category || null,
        sku: product.posMetaData.sku || null,
        batchId: product.posMetaData.batchId || null,
        batchName: product.posMetaData.batchName || null,
        alternateName: product.posMetaData.alternateName || null,
        labResultUrl: product.posMetaData.labResultUrl || null,
        packagedDate: product.posMetaData.packagedDate || null
      } : null,
      productAvailabilityByRetailer: Array.isArray(product.productAvailabilityByRetailer) ?
        product.productAvailabilityByRetailer.map((avail: any) => ({
          isAvailable: avail.isAvailable || false,
          retailer: {
            id: avail.retailer?.id || '',
            name: avail.retailer?.name || ''
          },
          productId: avail.productId || ''
        })) : [],

      // Include raw Dutchie data for debugging
      _dutchieRaw: product._dutchieRaw || undefined
    };

    return parsedProduct;
  }

  // Helper method to extract THC percentage from description
  public static extractTHCFromDescription(description: string): number | null {
    const thcMatch = description.match(/Total THC:?\s*(\d+\.?\d*)%/);
    if (thcMatch && thcMatch[1]) {
      return parseFloat(thcMatch[1]);
    }
    return null;
  }

  // Helper method to extract terpenes from description
  public static extractTerpenesFromDescription(description: string): Array<{name: string, percentage: number}> {
    const terpenesSection = description.match(/Total Terpenes:.*?(\d+\.?\d*)%.*?Top Terpenes:([\s\S]*?)(?:\n\n|$)/);
    if (!terpenesSection) return [];

    const terpeneLines = terpenesSection[2].split('\n').filter(line => line.includes('%'));
    return terpeneLines.map(line => {
      const match = line.match(/(.*?):\s*(\d+\.?\d*)%/);
      if (match) {
        return {
          name: match[1].trim(),
          percentage: parseFloat(match[2])
        };
      }
      return null;
    }).filter((terpene): terpene is {name: string, percentage: number} => terpene !== null);
  }

  public static async resolveRewards(rewardIds: string[]): Promise<any[]> {
    if (!rewardIds || rewardIds.length === 0) {
      return [];
    }

    const response = await fetch(`${this.baseUrl}/menu/resolve-rewards`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        rewardIds,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to resolve rewards: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data.rewards;
  }

  /**
   * Get products for a specific Dutchie special/deal
   * Uses the menu section filter to return only products included in the deal
   */
  public static async getProductsForSpecial(storeId: string, specialId: string): Promise<MenuProduct[]> {
    if (!storeId || !specialId) {
      return [];
    }

    try {
      const response = await fetch(`${this.baseUrl}/deals/${storeId}/specials/${specialId}/products`);

      if (!response.ok) {
        throw new Error(`Failed to get products for special: ${response.statusText}`);
      }

      const data = await response.json();

      // Parse the products using the same parseProduct method
      if (data.success && Array.isArray(data.data)) {
        return data.data.map((product: any) => this.parseProduct(product));
      }

      return [];
    } catch (error) {
      console.error('Error fetching products for special:', error);
      throw error;
    }
  }
}